// src/components/form/AsyncLookup.jsx
import React, { useEffect, useRef, useState } from 'react'

/**
 * Tiny async typeahead input.
 * Props:
 *  - value: { id, label } or null
 *  - onChange: (item|null) => void
 *  - fetchFn: async (query: string) => Promise<Array<{id,label}>>
 *  - placeholder: string
 *  - disabled: boolean
 */
export default function AsyncLookup({ value, onChange, fetchFn, placeholder = '', disabled = false }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value?.label || '')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const boxRef = useRef(null)

  // fetch when query changes (debounced 250ms)
  useEffect(() => {
    if (!fetchFn) return
    const h = setTimeout(async () => {
      try {
        if (!open && query.trim() === '') return
        setLoading(true); setError('')
        const list = await fetchFn(query.trim())
        setItems(Array.isArray(list) ? list : [])
      } catch (e) {
        setError(e.message || 'Lookup failed')
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => clearTimeout(h)
  }, [query, open, fetchFn])

  // close on click outside
  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return
      if (!boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [])

  useEffect(() => { if (value?.label) setQuery(value.label) }, [value?.label])

  return (
    <div ref={boxRef} className="position-relative">
      <input
        className="form-control"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          onChange(null) // clear selection while typing
          setQuery(e.target.value)
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && (
        <div
          className="card shadow-sm"
          style={{
            position: 'absolute',
            zIndex: 20,
            width: '100%',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {loading && <div className="p-2 text-muted">Loading…</div>}
          {error && <div className="p-2 text-danger">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div className="p-2 text-muted">No matches</div>
          )}
          {!loading && !error && items.map(it => (
            <button
              type="button"
              key={it.id}
              className="btn btn-link text-left w-100"
              style={{ whiteSpace: 'normal' }}
              onClick={() => { onChange(it); setOpen(false) }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
